import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { supabase } from '../lib/supabase';
import { Track, fetchTracks } from '../services/api';
import { useAppContext } from '../context/AppContext';

export interface FavoriteTrack extends Track {
  added_at: string;
}

// ── One shared favourites list for the whole site ─────────────
// Every useFavorites() (each ♡ button, the Favourite tracks list on Playlists) reads and
// updates this same store, so adding or removing a favourite anywhere shows up everywhere
// at once. It is loaded once per logged-in user, not once per heart.

type FavoritesState = {
  userId: string | null;
  favorites: FavoriteTrack[];
  ids: Set<string>;
  loading: boolean;
  error: string | null;
};

let state: FavoritesState = { userId: null, favorites: [], ids: new Set(), loading: false, error: null };
const listeners = new Set<() => void>();
let loadingFor: string | null = null;

const setState = (patch: Partial<FavoritesState>) => {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => state;

const setFavorites = (favorites: FavoriteTrack[]) =>
  setState({ favorites, ids: new Set(favorites.map((t) => t.id)) });

async function loadFavorites(userId: string) {
  loadingFor = userId;
  setState({ loading: true, error: null });
  try {
    const { data, error } = await supabase
      .from('user_favorites')
      .select('track_id, added_at')
      .eq('user_id', userId)
      .order('added_at', { ascending: false });
    if (error) throw error;

    let favorites: FavoriteTrack[] = [];
    if (data && data.length > 0) {
      // Full track data from the API, kept in the newest-first order from Supabase.
      const tracks = await fetchTracks(`?ids=${data.map((f) => f.track_id).join(',')}`);
      const trackMap = new Map(tracks.map((t) => [t.id, t]));
      favorites = data
        .map((f) => {
          const track = trackMap.get(f.track_id);
          return track ? { ...track, added_at: f.added_at } : null;
        })
        .filter(Boolean) as FavoriteTrack[];
    }

    // Ignore the answer if someone else logged in meanwhile.
    if (state.userId === userId) setFavorites(favorites);
  } catch (err: any) {
    if (state.userId === userId) setState({ error: err.message || 'Failed to fetch favorites.' });
  } finally {
    if (loadingFor === userId) loadingFor = null;
    if (state.userId === userId) setState({ loading: false });
  }
}

export const useFavorites = () => {
  const { user } = useAppContext();
  const userId = user?.id ?? null;
  const { favorites, ids, loading, error } = useSyncExternalStore(subscribe, getSnapshot);

  // New login (or logout): start a fresh list and load it once for everyone.
  useEffect(() => {
    if (state.userId === userId) return;
    setState({ userId, favorites: [], ids: new Set(), error: null, loading: false });
    if (userId && loadingFor !== userId) loadFavorites(userId);
  }, [userId]);

  const refetch = useCallback(async () => {
    if (state.userId) await loadFavorites(state.userId);
  }, []);

  // Optimistic: the heart and every list update at once, before the database answers.
  const toggleFavorite = useCallback(async (track: Track) => {
    const currentUser = state.userId;
    if (!currentUser) return;

    const isFav = state.ids.has(track.id);
    setFavorites(
      isFav
        ? state.favorites.filter((t) => t.id !== track.id)
        : [{ ...track, added_at: new Date().toISOString() }, ...state.favorites],
    );

    try {
      const { error } = isFav
        ? await supabase.from('user_favorites').delete().eq('user_id', currentUser).eq('track_id', track.id)
        : await supabase.from('user_favorites').insert({ user_id: currentUser, track_id: track.id });
      if (error) throw error;
    } catch (err: any) {
      // Saving failed: reload the real list and say why.
      setState({ error: err.message || 'Failed to update favorites.' });
      loadFavorites(currentUser);
    }
  }, []);

  const isFavorite = useCallback((trackId: string) => ids.has(trackId), [ids]);

  return { favorites, loading, error, toggleFavorite, isFavorite, refetch };
};